import React, { useEffect, useMemo } from 'react';
import { Loader, SdiHeart, SdiShip } from '@sdir/sds';
import styled from 'styled-components';
import { useIntl } from 'react-intl';
import { routes } from '../../../constants/routes';
import { useFeatureToggles, useCurrentPerson, useCurrentUserInfo } from '../../../services/hooks';
import {
  ToggleAssessment,
  ToggleDisableEducatia,
  ToggleHealth,
  isToggleOn,
} from '../../../constants/permissions';
import { useNavigateWithLang } from '../../../services/hooks/useNavigateWithLang';
import { MyCourses } from '../../Organisms/MyCourses/MyCourses';
import MyHealth from '../../Organisms/MyHealth/MyHealth';
import { MySeagoing } from '../../Organisms/MySeagoing/MySeagoing';
// eslint-disable-next-line max-len
import { MyAdditionalCompetence } from '../../Organisms/MyAdditionalCompetence/MyAdditionalCompetence';
import { MyAssessments } from '../../Organisms/MyAssessments/MyAssessments';
import { PageHeader } from '../../Atoms';
import { MyEducatia } from '../../Organisms/MyEducatia/MyEducatia';

type CompetenceDashboardProps = {
  onChangeLanguage: (l: string, skipRefresh?: boolean) => void;
};

export const CompetenceDashboard: React.FC<CompetenceDashboardProps> = ({ onChangeLanguage }) => {
  const intl = useIntl();
  const navigate = useNavigateWithLang();
  const featureToggle = useFeatureToggles();

  const {
    person: { loading, result: contactInfo },
  } = useCurrentPerson();
  const {
    userInfo: { isExternal },
  } = useCurrentUserInfo();

  useEffect(() => {
    const { search } = window.location;
    if (search.indexOf('transactionId') !== -1 || search.indexOf('responseCode') !== -1) {
      window.history.replaceState({}, document.title, `${window.location.pathname}#/`);
      window.sessionStorage.setItem('payment_window_step', '2');
      const sessionStorageUrl = window.sessionStorage.getItem('application_payment_step_url');
      if (sessionStorageUrl?.indexOf('/en/') !== -1) {
        onChangeLanguage('en', true);
      }
      navigate({
        pathname: sessionStorageUrl as string,
        search,
      });
    }
  }, [navigate, onChangeLanguage]);

  const backToHome = () => {
    navigate(routes.home);
  };

  const userPersonalNr = useMemo(() => {
    return contactInfo?.personalIdentificationNumber || '';
  }, [contactInfo]);

  const showHealth = !isToggleOn(featureToggle?.result, ToggleHealth, userPersonalNr);
  const showAssessment = !isToggleOn(featureToggle?.result, ToggleAssessment, userPersonalNr);
  const showEducatia = !isToggleOn(featureToggle?.result, ToggleDisableEducatia, userPersonalNr);

  if (loading) return <Loader />;

  return (
    <Wrapper>
      <PageHeader
        backText={intl.formatMessage({ id: 'back.to.dashboard' })}
        heading={intl.formatMessage({ id: 'competenceDashboard.page.title' })}
        primaryIcon={SdiShip}
        secondaryIcon={SdiHeart}
        backClick={backToHome}
      />
      <Content>
        <MyCourses />
        {showEducatia && <MyEducatia />}
        {showHealth && <MyHealth />}
        <MySeagoing />
        {showAssessment && !isExternal && <MyAssessments />}
        {!isExternal && <MyAdditionalCompetence />}
      </Content>
    </Wrapper>
  );
};

const Content = styled.div`
  display: flex;
  flex-direction: column;
`;

const Wrapper = styled.div`
  padding-top: 20px;
  padding-bottom: 60px;
`;
